// YouTube-style controls for product videos, only inside the expanded viewer (zoom-dialog).
// Product videos come without controls (just play/pause), so here each video gets a bar with a
// timeline to skip forward or back, play/pause, elapsed / total time and sound.
// The bar is added when the video element appears (deferred-media creates it on first play).

const ICONS = {
  play: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.5v13l11-6.5z" fill="currentColor"/></svg>',
  pause: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z" fill="currentColor"/></svg>',
  soundOn:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M4 9.5h3.5L12 6v12l-4.5-3.5H4z" fill="currentColor" stroke="none"/><path d="M15.5 9a4 4 0 0 1 0 6M18 6.5a7.5 7.5 0 0 1 0 11"/></svg>',
  muted:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M4 9.5h3.5L12 6v12l-4.5-3.5H4z" fill="currentColor" stroke="none"/><path d="m16 9.5 5 5m0-5-5 5"/></svg>',
};

/** @param {number} seconds */
const formatTime = (seconds) => {
  const s = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/** @param {HTMLVideoElement} video */
function enhance(video) {
  const host = video.closest('deferred-media') || video.parentElement;
  if (!host || video.dataset.dripBar) return;
  video.dataset.dripBar = 'true';
  host.querySelector('.drip-vbar')?.remove();

  const bar = document.createElement('div');
  bar.className = 'drip-vbar';
  bar.innerHTML = `
    <button type="button" class="drip-vbar__btn" data-action="play"></button>
    <span class="drip-vbar__time" data-current>0:00</span>
    <div class="drip-vbar__line" role="slider" tabindex="0" aria-label="Línea de tiempo del video" aria-valuemin="0">
      <div class="drip-vbar__rail"><div class="drip-vbar__buffered"></div><div class="drip-vbar__played"></div></div>
      <div class="drip-vbar__knob"></div>
    </div>
    <span class="drip-vbar__time" data-total>0:00</span>
    <button type="button" class="drip-vbar__btn" data-action="sound"></button>`;
  host.appendChild(bar);

  const playButton = /** @type {HTMLButtonElement} */ (bar.querySelector('[data-action="play"]'));
  const soundButton = /** @type {HTMLButtonElement} */ (bar.querySelector('[data-action="sound"]'));
  const line = /** @type {HTMLElement} */ (bar.querySelector('.drip-vbar__line'));
  const current = /** @type {HTMLElement} */ (bar.querySelector('[data-current]'));
  const total = /** @type {HTMLElement} */ (bar.querySelector('[data-total]'));

  let scrubbing = false;
  let resumeAfterScrub = false;
  let pendingFraction = /** @type {number | null} */ (null);
  let frame = 0;

  /** @param {number} fraction */
  const paint = (fraction) => {
    const f = Math.min(1, Math.max(0, fraction || 0));
    bar.style.setProperty('--played', String(f));
    const time = f * (video.duration || 0);
    current.textContent = formatTime(time);
    line.setAttribute('aria-valuenow', String(Math.round(time)));
    line.setAttribute('aria-valuetext', `${formatTime(time)} de ${formatTime(video.duration)}`);
  };

  const paintFromVideo = () => {
    if (!scrubbing && video.duration) paint(video.currentTime / video.duration);
  };

  const paintBuffered = () => {
    const { buffered, duration } = video;
    if (!duration || !buffered.length) return;
    bar.style.setProperty('--buffered', String(Math.min(1, buffered.end(buffered.length - 1) / duration)));
  };

  const paintDuration = () => {
    total.textContent = formatTime(video.duration);
    line.setAttribute('aria-valuemax', String(Math.round(video.duration || 0)));
    paintFromVideo();
  };

  const paintPlayButton = () => {
    const paused = video.paused || video.ended;
    playButton.innerHTML = paused ? ICONS.play : ICONS.pause;
    playButton.setAttribute('aria-label', paused ? 'Reproducir' : 'Pausar');
  };

  const paintSoundButton = () => {
    const muted = video.muted || video.volume === 0;
    soundButton.innerHTML = muted ? ICONS.muted : ICONS.soundOn;
    soundButton.setAttribute('aria-label', muted ? 'Activar sonido' : 'Silenciar');
  };

  // Smooth progress while playing (timeupdate alone only fires about 4 times per second).
  const tick = () => {
    paintFromVideo();
    frame = video.paused ? 0 : requestAnimationFrame(tick);
  };

  video.addEventListener('play', () => {
    paintPlayButton();
    if (!frame) frame = requestAnimationFrame(tick);
  });
  video.addEventListener('pause', paintPlayButton);
  video.addEventListener('ended', paintPlayButton);
  video.addEventListener('timeupdate', paintFromVideo);
  video.addEventListener('loadedmetadata', paintDuration);
  video.addEventListener('durationchange', paintDuration);
  video.addEventListener('progress', paintBuffered);
  video.addEventListener('volumechange', paintSoundButton);

  playButton.addEventListener('click', (event) => {
    event.stopPropagation();
    if (video.paused || video.ended) video.play().catch(() => {});
    else video.pause();
  });

  soundButton.addEventListener('click', (event) => {
    event.stopPropagation();
    video.muted = !video.muted;
    if (!video.muted && video.volume === 0) video.volume = 1;
  });

  // Timeline: tap to jump, drag to scrub. The video pauses while scrubbing (like YouTube)
  // and resumes on release if it was playing.
  /** @param {number} clientX */
  const fractionAt = (clientX) => {
    const rect = line.getBoundingClientRect();
    return rect.width ? Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)) : 0;
  };

  /** @param {number} fraction */
  const seek = (fraction) => {
    paint(fraction);
    pendingFraction = fraction;
    requestAnimationFrame(() => {
      if (pendingFraction === null || !video.duration) return;
      video.currentTime = pendingFraction * video.duration;
      pendingFraction = null;
    });
  };

  line.addEventListener('pointerdown', (event) => {
    if (!video.duration) return;
    event.preventDefault();
    event.stopPropagation();
    line.setPointerCapture(event.pointerId);
    scrubbing = true;
    resumeAfterScrub = !video.paused && !video.ended;
    video.pause();
    bar.classList.add('is-scrubbing');
    seek(fractionAt(event.clientX));
  });

  line.addEventListener('pointermove', (event) => {
    if (scrubbing) seek(fractionAt(event.clientX));
  });

  const endScrub = () => {
    if (!scrubbing) return;
    scrubbing = false;
    bar.classList.remove('is-scrubbing');
    if (resumeAfterScrub) video.play().catch(() => {});
  };
  line.addEventListener('pointerup', endScrub);
  line.addEventListener('pointercancel', endScrub);
  line.addEventListener('lostpointercapture', endScrub);

  line.addEventListener('keydown', (event) => {
    if (!video.duration) return;
    const steps = { ArrowLeft: -5, ArrowRight: 5, ArrowDown: -5, ArrowUp: 5 };
    let time = null;
    if (event.key in steps) time = video.currentTime + steps[/** @type {keyof typeof steps} */ (event.key)];
    else if (event.key === 'Home') time = 0;
    else if (event.key === 'End') time = video.duration;
    if (time === null) return;
    event.preventDefault();
    video.currentTime = Math.min(video.duration, Math.max(0, time));
    paintFromVideo();
  });

  // Clicks on the bar never reach the video's own play/pause overlay or the gallery.
  bar.addEventListener('click', (event) => event.stopPropagation());

  // Pause when the shopper swipes to another photo or closes the viewer.
  const slide = video.closest('li') || host;
  new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.intersectionRatio < 0.5) && !video.paused) video.pause();
    },
    { threshold: [0, 0.5] }
  ).observe(slide);
  video.closest('dialog')?.addEventListener('close', () => video.pause());

  paintPlayButton();
  paintSoundButton();
  paintDuration();
  paintBuffered();
  if (!video.paused && !frame) frame = requestAnimationFrame(tick);
}

const enhanceAll = () => {
  document.querySelectorAll('zoom-dialog video').forEach((video) => enhance(/** @type {HTMLVideoElement} */ (video)));
};

enhanceAll();
// Videos are created on first play (and the gallery can be re-rendered on variant change).
new MutationObserver((mutations) => {
  const videoAdded = mutations.some((mutation) =>
    [...mutation.addedNodes].some((node) => node instanceof Element && (node.matches('video') || node.querySelector('video')))
  );
  if (videoAdded) enhanceAll();
}).observe(document.body, { childList: true, subtree: true });
